import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  IconButton,
  useTheme,
  Collapse,
  ListItemButton,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TimelineIcon from '@mui/icons-material/Timeline';
import SensorsIcon from '@mui/icons-material/Sensors';
import CompareIcon from '@mui/icons-material/Compare';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import { fetchTunnelConfig } from '../api/sensorApi';

const drawerWidth = 240;

const Sidebar = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [tunnels, setTunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load tunnels from config file using API
  useEffect(() => {
    const loadTunnels = async () => {
      try {
        const tunnelConfigData = await fetchTunnelConfig();
        setTunnels(tunnelConfigData);
        setLoading(false);
      } catch (err) {
        console.error('Error loading tunnel config:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    
    loadTunnels();
  }, []);
  
  // No longer need tunnel expansion since we navigate directly to tunnel page
  
  // Base menu items
  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'All Sensors', icon: <SensorsIcon />, path: '/sensors' },
    { text: 'Comparison View', icon: <CompareIcon />, path: '/comparison' },
    { text: 'Predictions', icon: <AssessmentIcon />, path: '/predictions' },
    { text: 'Wash History', icon: <CleaningServicesIcon />, path: '/washes' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        padding: theme.spacing(1, 1),
        justifyContent: 'flex-end'
      }}>
        <IconButton onClick={onClose}>
          <ChevronLeftIcon />
        </IconButton>
      </Box>
      <Divider />
      <List>
        {/* Main menu items */}
        {menuItems.map((item) => (
          <ListItem 
            button 
            key={item.text} 
            onClick={() => {
              navigate(item.path);
              onClose();
            }}
            selected={location.pathname === item.path}
            sx={{
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.light + '30',
              },
              '&.Mui-selected:hover': {
                backgroundColor: theme.palette.primary.light + '40',
              },
            }}
          >
            <ListItemIcon>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
        
        <Divider sx={{ my: 1 }} />
        
        {/* Tunnels */}
        <ListItem>
          <ListItemText primary="Tunnels" primaryTypographyProps={{ fontWeight: 'bold' }} />
        </ListItem>
        
        {loading ? (
          <ListItem>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          </ListItem>
        ) : error ? (
          <ListItem>
            <ListItemText 
              primary="Error loading tunnels" 
              secondary="Check console for details"
              primaryTypographyProps={{ color: 'error' }}
            />
          </ListItem>
        ) : tunnels.map((tunnel) => (
          <React.Fragment key={tunnel.id}>
            <ListItem 
              button 
              onClick={() => {
                navigate(`/tunnels/${tunnel.id}`);
                onClose();
              }}
              selected={location.pathname.startsWith(`/tunnels/${tunnel.id}`)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.light + '30',
                },
                '&.Mui-selected:hover': {
                  backgroundColor: theme.palette.primary.light + '40',
                },
              }}
            >
              <ListItemIcon>
                <TimelineIcon />
              </ListItemIcon>
              <ListItemText primary={tunnel.name} />
            </ListItem>
            
            {/* Sections within this tunnel */}
            {/* No more expandable menu - just navigate directly to tunnel */}
          </React.Fragment>
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar;